<template>
	<v-dialog v-model="dialog" max-width="960px" persistent transition="dialog-bottom-transition" scrollable hide-overlay>
		<v-card>
			<v-toolbar card dark color="info">
				<v-btn icon dark @click.native="closeModal(false)">
					<v-icon>close</v-icon>
				</v-btn>
				<v-toolbar-title>{{ title }}</v-toolbar-title>
				<v-spacer></v-spacer>
				<div v-if="selectedTemplateId !== null">
					<v-btn flat @click="onBackBtnClick"><v-icon left>chevron_left</v-icon> Back</v-btn>
					<v-btn dark class="action-btn" @click="onUseTemplateClick(selectedTemplate)">Use Template</v-btn>
				</div>
			</v-toolbar>
			<v-card-text>
				<div v-if="selectedTemplateId === null">
					<v-layout>
						<v-flex shrink>
							<v-layout class="mr-3">
								<v-flex md12>
									<div class="horizontal">
										<div class="tile-structure">
											<div class="tile-structure-children">
												<div class="add-item-squircle-box" @click="onBlankTemplateChosen">
													<v-icon>add</v-icon>
												</div>
											</div>
											<div class="tile-structure-name">Start Blank</div>
										</div>
									</div>
								</v-flex>
							</v-layout>
						</v-flex>
						<v-flex>
							<h2 class="text-xs-left">Start from a template</h2>
							<v-text-field ref="search" v-debounce:300ms="updateSearch" append-icon="search" label="Search Templates" autofocus="" background-color="#fff"></v-text-field>
							<v-list subheader class="mt-3">
								<v-subheader v-if="filteredTemplates.length === 0 && search != null">No search results found</v-subheader>
								<v-list-tile v-for="template in filteredTemplates" :key="template.id" avatar @click="onTemplateClick(template)">
									<v-list-tile-avatar>
										<v-icon class="info white--text">mdi-file-document-outline</v-icon>
									</v-list-tile-avatar>

									<v-list-tile-content>
										<v-list-tile-title>{{ template.title }}</v-list-tile-title>
										<v-list-tile-sub-title v-if="isTemplateInUse(template.id)">This template has already been used by your organisation</v-list-tile-sub-title>
									</v-list-tile-content>

									<v-list-tile-action>
										<v-icon color="grey lighten-1">chevron_right</v-icon>
									</v-list-tile-action>
								</v-list-tile>
							</v-list>
						</v-flex>
					</v-layout>
				</div>

				<div v-if="selectedTemplateId !== null">
					<global-position-template :id="selectedTemplateId" />
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
			</v-card-actions>
		</v-card>
		<v-dialog v-model="showNameDialog" transition="dialog-bottom-transition" scrollable max-width="500">
			<v-card>
				<v-card-title>
					<h2 class="text-xs-left">Give the new template a title</h2>
				</v-card-title>
				<v-card-text>
					<v-divider></v-divider>

					<v-card-text>
						<v-text-field ref="newTemplateTitle" v-model="newTemplate.title" :error-messages="titleErrors" label="Template Title" required></v-text-field>

						<v-select v-model="newTemplate.department" :items="departmentsOrdered" hint="Leave as N/A if necessary" persistent-hint :error-messages="departmentErrors" item-text="name" return-object label="Which department is this template relevent to?" required></v-select>
					</v-card-text>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="blue darken-1" flat @click="showNameDialog = false">Close</v-btn>
					<v-btn color="blue darken-1" flat @click="onTemplateSelected(selectedTemplate)">Save</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-dialog>
</template>

<script>
	import { mapGetters } from 'vuex';
	import { validationMixin } from 'vuelidate';
	import { required } from 'vuelidate/lib/validators';
	import GlobalPositionTemplate from '@/components/positionTemplate/GlobalPositionTemplate';

	export default {
		components: {
			GlobalPositionTemplate
		},
		props: {
			templates: {
				type: Array,
				required: true
			},
			globalTemplatesInUse: {
				type: Array,
				required: true
			}
		},
		data() {
			return {
				selectedTemplateId: null,
				dialog: true,
				search: '',
				showNameDialog: false,
				newTemplate: {
					title: '',
					department: { id: null, name: 'N/A' }
				}
			};
		},
		mixins: [validationMixin],
		mounted() {
			this.$nextTick(() => {
				this.$refs.search.focus();
			});
		},
		watch: {
			showNameDialog(newVal) {
				if (newVal) {
					this.focusNewTemplateTitle();
				}
			}
		},
		validations: {
			newTemplate: {
				title: { required },
				department: { required }
			}
		},
		computed: {
			...mapGetters({
				departments: 'departments/departments'
			}),
			title() {
				return this.selectedTemplateId != null ? this.templates.find((x) => x.id === this.selectedTemplateId).title : 'New Position Template';
			},
			selectedTemplate() {
				if (this.templates !== null && this.templates.length > 0 && this.selectedTemplateId !== null) {
					return this.templates.find((x) => x.id === this.selectedTemplateId);
				}
				return null;
			},
			filteredTemplates() {
				return this.templates.filter((x) => x.title.toLowerCase().includes(this.search.toLowerCase()));
			},
			departmentsOrdered() {
				var departments = this.departments.slice(0);
				var sorted = departments.sort();
				sorted.unshift({ id: null, name: 'N/A' });
				return sorted;
			},
			departmentErrors() {
				const errors = [];
				if (!this.$v.newTemplate.department.$dirty) return errors;
				!this.$v.newTemplate.department.required && errors.push('Department is required.');
				return errors;
			},
			titleErrors() {
				const errors = [];
				if (!this.$v.newTemplate.title.$dirty) return errors;
				!this.$v.newTemplate.title.required && errors.push('Template title is required.');
				return errors;
			},
		},
		methods: {
			onBlankTemplateChosen() {
				this.$emit('onBlankTemplateSelected');
			},
			onUseTemplateClick(template) {
				this.newTemplate.title = template.title;
				this.newTemplate.department = { id: null, name: 'N/A' }
				this.showNameDialog = true;
			},
			onTemplateSelected(template) {
				this.$v.newTemplate.$touch();
				if (this.$v.newTemplate.$invalid) return;

				const selectedVal = {
					templateId: template.id,
					title: this.newTemplate.title,
					department: this.newTemplate.department
				}
				this.$emit('onTemplateSelected', selectedVal);
			},
			onTemplateClick(template) {
				this.selectedTemplateId = template.id;
			},
			onBackBtnClick() {
				this.selectedTemplateId = null;
			},
			closeModal() {
				this.selectedTemplateId = null;
				this.$emit('closeModal');
			},
			isTemplateInUse(templateId) {
				return this.globalTemplatesInUse.includes(templateId);
			},
			updateSearch(val) {
				this.search = val;
			},
			focusNewTemplateTitle() {
				this.$nextTick(() => {
					this.$refs.newTemplateTitle.focus();
				});
			}
		}
	};
</script>

<style scoped>
	.horizontal {
		align-items: center;
		display: flex;
	}
	.tile-structure {
		border-radius: 30px;
		-moz-box-sizing: border-box;
		box-sizing: border-box;
		height: 211px;
		min-width: 150px;
		padding: 15px 0;
		width: 150px;
		align-items: center;
		background: rgba(246, 248, 249, 0);
		display: flex;
		flex-flow: column;
		position: relative;
		transition-duration: 0.4s;
	}
	.tile-structure:hover:not(.disabled) {
		background: #f6f8f9;
		transform: translate3d(0, -5px, 0);
		cursor: pointer;
	}
	.tile-structure-name {
		box-sizing: border-box;
		margin-top: 10px;
		padding: 0 10px;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		color: #151b26;
		display: -webkit-box;
		font-size: 13px;
		font-weight: 500;
		line-height: 17px;
		max-height: 34px;
		overflow: hidden;
		text-align: center;
		text-overflow: ellipsis;
		width: 100%;
	}
	.tile-structure-children {
		height: 120px;
		width: 120px;
	}
	.add-item-squircle-box {
		border-radius: 20px;
		height: 100%;
		width: 100%;
		align-items: center;
		background: #fff;
		border: 2px dashed;
		-moz-box-sizing: border-box;
		box-sizing: border-box;
		color: #9ca6af;
		display: flex;
		fill: #9ca6af;
		justify-content: center;
		position: relative;
	}

	.template-card {
		border-radius: 20px;
		height: 120px;
		width: 120px;
		align-items: center;
		background-color: #8da3a6;
		display: flex;
		flex-direction: column;
		justify-content: center;
		position: relative;
	}
	.template-card.blue {
		background-color: #4186e0;
	}
	.template-card .v-icon {
		color: #fff;
		font-size: 50px;
	}
</style>
